import { nomeDoSistema } from '@/lib/sistemaDaClinica';
import { useEffect, useState } from 'react';
import Dialogo from '@/components/Dialogo';
import CamposValores, { valoresIniciais } from '@/components/leads/CamposValores';
import { etapaPorId } from '@/lib/constants';
import { motivoEncerra, motivoPorId, motivosAtivos } from '@/leads/motivos';
import { ETAPAS_COM_NEGOCIADOR, exigeDataDeRetorno } from '@/leads/regras';
import { dataDeRetornoPadrao } from '@/leads/retorno';
import { format } from 'date-fns';
import { paraNumero } from '@/leads/recebimentos';
import { moverEtapa, renomearLead } from '@/services/leadsService';
import {
  buscarAgendamentosDoDia,
  criarAgendamento,
  ouvirAgendamentosDoLead,
  reagendar,
} from '@/services/agendamentosService';
import { chaveDia } from '@/agenda/grade';
import CampoAgendamento from '@/components/leads/CampoAgendamento';
import { apenasNegociadores, opcoesComGravado } from '@/equipe/funcoes';
import { tratarErro } from '@/lib/erros';
// ★★★ PC-6 — a AGENDA: agendando um lead que não é de quem agenda.
import { usePosse } from '@/leads/usePosse';
import { agendandoLeadDeOutra, clinicaDeUmaSdr } from '@/leads/posseTela';
import { assumirLeadParaMim } from '@/services/leadsService';

/**
 * Pede o campo que a etapa exige antes de concluir a mudança: valor para
 * "fechado", motivo para "perdido" (PLANO 4.3 — as security rules recusam
 * a escrita sem eles, então a UI precisa coletar antes de tentar) e, desde a V4,
 * o NEGOCIADOR para "compareceu"/"em_negociacao". Desde a VE ele também pede o
 * VALOR ORÇADO (a oportunidade), e por isso abre enquanto faltar um dos dois —
 * ver `exigeCampoExtra`. Ambos são exigência só do cliente (`regras.js`).
 *
 * `clinicaId`, `autor` e `equipe` chegam por PROPS, não pelo `useLeads`: desde a
 * V2 o diálogo também abre no painel da conversa (`/conversas`), rota que não
 * tem `LeadsProvider`. Quem está dentro do provider só repassa o que já tem.
 */
/**
 * VG — `config` (a config da clínica) traz as duas listas de motivo. As colunas
 * "Não agendou" e "Não fechou" exigem motivo; "Não fechou" exige também o
 * negociador (é ele quem preenche). O `encerra` do motivo escolhido é gravado
 * no lead pelo `moverEtapa`, nunca inferido depois.
 */
export default function DialogoEtapa({ pendente, aoFechar, clinicaId, autor, equipe = [], grade = null, config = null }) {
  /** ★ WD-c2: os cinco valores num objeto só — é o contrato do `CamposValores`, compartilhado com a ficha. */
  const [valores, setValores] = useState(() => valoresIniciais(null));
  const [motivo, setMotivo] = useState('');
  const [retorno, setRetorno] = useState(''); // VJ: 'yyyy-MM-ddTHH:mm' do datetime-local
  const [negociadorId, setNegociadorId] = useState('');
  const [orcado, setOrcado] = useState('');
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);

  // VF-1 — agendamento no mesmo ato de mover para "agendado".
  const [dia, setDia] = useState(() => chaveDia(new Date()));
  const [hora, setHora] = useState('');
  const [tipo, setTipo] = useState('avaliacao');
  const [foraDaGrade, setForaDaGrade] = useState(false);
  const [acao, setAcao] = useState('nova');
  const [agendaDoDia, setAgendaDoDia] = useState([]);
  const [carregandoAgenda, setCarregandoAgenda] = useState(false);
  const [futuros, setFuturos] = useState([]);
  // VQ-correção 1: a detecção de "remarcar" falhando é aviso visível, não silêncio.
  const [erroFuturos, setErroFuturos] = useState(null);

  const aberto = Boolean(pendente);
  const paraFechado = pendente?.etapa === 'fechado';
  const paraNaoAgendou = pendente?.etapa === 'nao_agendou';
  const paraNaoFechou = pendente?.etapa === 'nao_fechou';
  const comMotivo = paraNaoAgendou || paraNaoFechou;
  // A lista da etapa, da MESMA fonte que a aba de Configurações semeia.
  const listaMotivos = comMotivo ? motivosAtivos(config, pendente.etapa) : [];
  const motivoAtual = motivo || listaMotivos[0]?.id || '';
  /**
   * ★ VJ — DATA DE RETORNO: obrigatória quando o motivo escolhido NÃO encerra;
   * o campo SOME quando encerra (lead encerrado não tem próximo contato).
   * Vem preenchido com a cadência da clínica para a etapa de destino, editável.
   * A tela decide pela MESMA regra pura do serviço (`exigeDataDeRetorno`).
   */
  const motivoAtualEncerra = comMotivo ? motivoEncerra(config, pendente.etapa, motivoAtual) : false;
  /**
   * ★★ WI — `pedeRetorno` DEIXOU DE DEPENDER DE `comMotivo`.
   *
   * Ele estava amarrado ao motivo porque, até aqui, só as duas colunas de "não" COM motivo
   * pediam retorno. A WI acrescentou `nao_compareceu` — que **não tem motivo** (decisão da
   * VG: *"Não compareceu não tem"*). Com o `comMotivo &&` na frente, o campo simplesmente
   * não apareceria, `validarEtapa` recusaria por falta de data, e a SDR ficaria num beco sem
   * saída: erro na tela e nenhum lugar para digitar.
   *
   * ★ `motivoAtualEncerra` já nasce `false` quando não há motivo (linha acima), então a
   * regra continua exata: motivo que ENCERRA dispensa o retorno; sem motivo, exige.
   */
  // ★ `pendente?.etapa`, com a interrogação: o `comMotivo &&` que estava aqui na frente
  // fazia DUAS coisas — decidia a regra e protegia do `pendente` nulo (o diálogo é montado
  // fechado). Tirar um sem ver o outro derrubou 119 casos de uma vez.
  const pedeRetorno = exigeDataDeRetorno({ etapa: pendente?.etapa, encerra: motivoAtualEncerra });
  const retornoPadrao = pedeRetorno
    ? format(dataDeRetornoPadrao(pendente?.lead, pendente.etapa, config), "yyyy-MM-dd'T'HH:mm")
    : '';
  const retornoAtual = retorno === null ? '' : (retorno || retornoPadrao);
  const paraNegociacao = ETAPAS_COM_NEGOCIADOR.includes(pendente?.etapa);
  // ★ WH: só `compareceu` entra no modo novo — `nao_fechou` segue com o campo solto.
  const paraComparecimento = pendente?.etapa === 'compareceu';
  const paraAgendado = pendente?.etapa === 'agendado';
  /**
   * ★★★ PC-6 (dono, 24/10) — agendar um lead que NÃO é seu:
   *   liberado → "Assumir e agendar": a callable assume primeiro (trava, tarefas, trilha) e só então
   *              a consulta é marcada — quem agenda é quem fica com o lead;
   *   travado  → aviso com o motivo; NÃO bloqueia: o lead (e a comissão) continua com a dona.
   * ⚠ O papel não muda o ESTADO da situação (só o "por cima"), então não precisa vir por prop.
   * Clínica com 1 SDR: o aviso de trava some (o comportamento continua).
   */
  const posse = usePosse({ lead: pendente?.lead, clinicaId, usuarioId: autor?.id ?? null, ativo: paraAgendado && Boolean(pendente?.lead?.id) });
  const deOutra = paraAgendado ? agendandoLeadDeOutra(posse) : null;
  const avisoDePosse = deOutra?.aviso && !clinicaDeUmaSdr(equipe) ? deOutra.aviso : null;

  /**
   * A agenda do dia escolhido — LEITURA ÚNICA, não assinatura: o diálogo
   * consulta ao trocar a data e fecha. Um listener aberto por tecla digitada
   * seria desperdício num diálogo que vive segundos.
   */
  useEffect(() => {
    if (!paraAgendado || !aberto || !dia || !clinicaId) return undefined;
    let vivo = true;
    setCarregandoAgenda(true);
    const [a, m, d] = dia.split('-').map(Number);
    buscarAgendamentosDoDia(clinicaId, new Date(a, m - 1, d, 12))
      .then((lista) => { if (vivo) setAgendaDoDia(lista); })
      .catch(() => { if (vivo) setErro('Não foi possível carregar a agenda do dia.'); })
      .finally(() => { if (vivo) setCarregandoAgenda(false); });
    return () => { vivo = false; };
  }, [paraAgendado, aberto, dia, clinicaId]);

  /** Consultas já marcadas deste lead, para oferecer remarcar em vez de duplicar. */
  useEffect(() => {
    if (!paraAgendado || !aberto || !clinicaId || !pendente?.lead?.id) return undefined;
    setErroFuturos(null);
    return ouvirAgendamentosDoLead(clinicaId, pendente.lead.id, (lista) => { setErroFuturos(null); setFuturos(lista); }, (e) => {
      // ★ WT 1b: passa a REGISTRAR (o aviso na tela já existia, da VQ)
      tratarErro('as consultas do lead', null, 'service')(e);
      setErroFuturos('Não foi possível conferir as consultas já marcadas deste lead. Tente de novo; se persistir, avise o suporte.');
    });
  }, [paraAgendado, aberto, clinicaId, pendente?.lead?.id]);

  const agora = Date.now();
  const agendamentoFuturo = futuros.find(
    (a) => a?.status === 'agendado' && (a.dataHora?.toDate?.()?.getTime() ?? 0) > agora,
  ) ?? null;
  // VP: só quem exerce a função de negociador — mais o já gravado, se estiver fora da lista.
  const negociadores = opcoesComGravado(apenasNegociadores(equipe), pendente?.lead?.negociadorId, pendente?.lead?.negociadorNome);

  /**
   * VE — os dois campos vêm PREENCHIDOS com o que o lead já tem.
   *
   * Desde a VE o diálogo abre quando falta o negociador **ou** o orçado; quem já
   * escolheu o negociador e vem só informar o orçamento veria um select vazio, e
   * confirmar apagaria o negociador gravado. Derivar do lead resolve sem efeito:
   * o estado local só existe depois que a pessoa mexe no campo.
   */
  /** ★ Os valores nascem do LEAD: código e nome já vêm preenchidos, e a oportunidade também, se houver. */
  useEffect(() => {
    if (pendente?.lead) setValores(valoresIniciais(pendente.lead));
  }, [pendente?.lead?.id]);

  const negociadorAtual = negociadorId || pendente?.lead?.negociadorId || '';
  const orcadoAtual = orcado || (Number(pendente?.lead?.valorOrcado) > 0
    ? String(pendente.lead.valorOrcado)
    : '');

  function fechar() {
    setValores(valoresIniciais(null));
    setMotivo('');
    setRetorno('');
    setNegociadorId('');
    setOrcado('');
    setDia(chaveDia(new Date()));
    setHora('');
    setTipo('avaliacao');
    setForaDaGrade(false);
    setAcao('nova');
    setAgendaDoDia([]);
    setFuturos([]);
    setErro(null);
    aoFechar();
  }

  async function confirmar(evento) {
    evento.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      /**
       * ★ AGENDADO NÃO PASSA POR `moverEtapa`.
       *
       * `criarAgendamento` já grava o agendamento, move a etapa e escreve a
       * timeline NO MESMO LOTE — atômico. Chamar `moverEtapa` aqui criaria um
       * segundo caminho de escrita para o mesmo fato, e é justamente o que a
       * peça existe para eliminar: lead em "agendado" sem consulta marcada.
       */
      if (paraAgendado) {
        if (!dia || !hora) throw new Error('Escolha o dia e o horário da consulta.');
        const [a, m, d] = dia.split('-').map(Number);
        const [hh, mm] = hora.split(':').map(Number);
        const dataHora = new Date(a, m - 1, d, hh, mm, 0, 0);

        if (acao === 'remarcar' && agendamentoFuturo) {
          await reagendar(clinicaId, agendamentoFuturo, dataHora, autor, { lead: pendente.lead });
        } else {
          // ★ PC-6: "Assumir e agendar" — assume ANTES; se a callable recusar, nada é marcado.
          let leadDoAgendamento = pendente.lead;
          if (deOutra?.assumirAntes) {
            await assumirLeadParaMim(clinicaId, pendente.lead);
            leadDoAgendamento = { ...pendente.lead, sdrId: autor?.id ?? '', sdrNome: autor?.nome ?? '' };
          }
          await criarAgendamento(
            clinicaId,
            leadDoAgendamento,
            { dataHora, tipo, foraDaGrade, procedimento: pendente.lead?.procedimento ?? '' },
            autor,
          );
        }
        fechar();
        return;
      }

      // VJ: motivo vivo → a data de retorno é obrigatória, e vira tarefa no lote.
      let retornoEm;
      if (pedeRetorno) {
        retornoEm = retornoAtual ? new Date(retornoAtual) : null;
        if (!retornoEm || Number.isNaN(retornoEm.getTime())) throw new Error('Informe a data do retorno.');
      }

      let extras;
      if (paraFechado) {
        /**
         * ★★★ INCIDENTE 18/10 — o negociador escolhido VAI JUNTO no fechamento.
         *
         * ⚠ Desde a WO 3c (04/out) `fechado` está em `ETAPAS_COM_NEGOCIADOR`: o select aparece,
         * mas este ramo não levava a escolha — lead sem negociador gravado não fechava (553 em
         * Campinas), e trocar o negociador no fechamento era descartado em silêncio.
         * ★ O gravado FORA DA FUNÇÃO (`opcoesComGravado`) não é enviado: o `moverEtapa` só grava
         * o campo quando ele vem no `extras`, então omitir mantém o gravado. ⚠ Mandar `''` ou o
         * rótulo "(fora da função)" apagaria/sujaria o nome. Ver `docs/incidente-fechar-negociador-testes.md`.
         */
        const escolhido = negociadores.find((u) => u.id === negociadorAtual);
        extras = {
          ...(escolhido && !escolhido.foraDaFuncao
            ? { negociadorId: escolhido.id, negociadorNome: escolhido.nome ?? '' }
            : {}),
          valorFechado: paraNumero(valores.valorFechado),
          // VA: entrada é campo do LEAD (não recebimento) — o SDR pode gravar.
          valorEntrada: paraNumero(valores.valorEntrada),
          codigoCareflow: String(valores.codigoCareflow ?? '').trim(),
          // ★ WD-c1: a oportunidade também entra no fechamento — um conjunto de campos só, dois momentos.
          valorOrcado: paraNumero(valores.valorOrcado),
        };
      }
      else if (paraNaoAgendou) {
        extras = {
          motivoNaoAgendou: motivoAtual,
          motivoRotulo: motivoPorId(config, 'nao_agendou', motivoAtual)?.rotulo ?? motivoAtual,
          encerra: motivoEncerra(config, 'nao_agendou', motivoAtual),
        };
      }
      else {
        const escolhido = negociadores.find((u) => u.id === negociadorAtual);
        extras = {
          negociadorId: escolhido?.id ?? '',
          negociadorNome: escolhido?.nome ?? '',
          /**
           * ★★ WH — o comentário da VE que estava aqui deixou de ser verdade.
           *
           * Ele dizia: "em branco significa 'o orçamento ainda não saiu' — grava 0 e o
           * badge do card cobra depois. Não é motivo para travar a etapa." Medido em
           * setembro: **38 dos 59** comparecimentos ficaram sem valor e **47 sem
           * negociador** — o badge cobrou e ninguém atendeu. Agora EM BRANCO TRAVA, e
           * quem não teve orçamento diz isso com a caixa.
           */
          valorOrcado: paraNumero(paraComparecimento ? valores.valorOrcado : orcadoAtual),
          // ★ Os três só existem no comparecimento: a porta valida, e o serviço grava a
          // marca e os carimbos no MESMO lote da etapa.
          ...(paraComparecimento
            ? {
                codigoCareflow: String(valores.codigoCareflow ?? '').trim(),
                semOrcamento: valores.semOrcamento === true,
                nomeConfirmado: valores.nomeConfirmado === true,
                nome: String(valores.nome ?? '').trim(),
              }
            : {}),
          // VG: "Não fechou" leva o motivo junto com o negociador.
          ...(paraNaoFechou
            ? {
                motivoNaoFechou: motivoAtual,
                motivoRotulo: motivoPorId(config, 'nao_fechou', motivoAtual)?.rotulo ?? motivoAtual,
                encerra: motivoEncerra(config, 'nao_fechou', motivoAtual),
              }
            : {}),
        };
      }
      /**
       * ★★ WI — O `retornoEm` ENTRA NUM LUGAR SÓ, depois dos ramos.
       *
       * Ele estava espalhado DENTRO dos ramos com motivo (`nao_agendou` e `nao_fechou`), e por
       * isso `nao_compareceu` — que não tem motivo e entrou na WI — saía do diálogo SEM a data:
       * a tela pedia, a pessoa digitava, e `moverEtapa` recusava com "Informe a data do
       * retorno". O beco sem saída, de novo, com o campo na tela.
       *
       * ★ Quem decide se há data é `pedeRetorno`, que é decidido pela ETAPA — então o lugar
       * certo de aplicá-la é aqui, uma vez, e não em cada ramo. Achado pelo caso de tela que a
       * mutação I15 obrigou a escrever.
       */
      if (retornoEm) extras.retornoEm = retornoEm;
      await moverEtapa(clinicaId, pendente.lead, pendente.etapa, extras, autor);
      /**
       * ★★ WD-c1 — o NOME vai DEPOIS, e por caminho PRÓPRIO.
       *
       * O nome é campo único (antes havia `nome` e `nomeCompleto`), e não é campo comum: `renomearLead`
       * renomeia, no MESMO lote, o `contatoNomeSalvo` das conversas de vendas vinculadas e registra a linha na
       * timeline (VM). Metê-lo no `extras` do `moverEtapa` gravaria só o lead, e a mesma pessoa ficaria com
       * dois nomes na tela — o problema que a VM resolveu.
       *
       * ★ LIMITE DECLARADO: são DUAS escritas, não uma transação. Se a segunda falhar, a etapa já mudou e o
       * nome não — o erro aparece na tela e a pessoa corrige o nome pela ficha. Escolhi isso em vez de
       * afrouxar o `renomearLead` (que é o que mantém conversa e lead com o mesmo nome).
       */
      // ★★ WH: a renomeação passou a valer TAMBÉM ao entrar em "compareceu" — antes
      // era só no fechamento, e o nome do WhatsApp atravessava a avaliação inteira
      // (medido: 8 dos 59 comparecimentos de setembro com nome de `pushName`).
      const nomeNovo = String(valores.nome ?? '').trim();
      const renomeiaAqui = paraFechado || pendente.etapa === 'compareceu';
      if (renomeiaAqui && nomeNovo && nomeNovo !== (pendente.lead?.nome ?? '').trim()) {
        await renomearLead(clinicaId, pendente.lead, nomeNovo, autor);
      }
      fechar();
    } catch (e) {
      setErro(e.message ?? 'Não foi possível mover o lead.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Dialogo
      titulo={`Mover para ${etapaPorId(pendente?.etapa ?? '').label}`}
      aberto={aberto}
      aoFechar={fechar}
    >
      <form className="formulario" onSubmit={confirmar}>
        <p className="dialogo-contexto">
          Lead: <strong>{pendente?.lead.nome}</strong>
        </p>

        {/* ★★ WD-c2 — O MESMO componente que o botão "Editar" da ficha abre. Não é uma cópia: é
            `CamposValores`, um só, usado nos dois momentos (fechar e editar). Duas cópias divergiriam no
            primeiro ajuste, e o mesmo lead teria regras diferentes conforme o caminho.
            ★ O motivo não aparece aqui porque `exigeMotivo` sabe que não há total anterior — ao FECHAR não
            se justifica o primeiro registro do número. A regra é a mesma função nos dois momentos. */}
        {paraFechado && (
          <>
            <CamposValores
              valores={valores}
              aoMudar={setValores}
              lead={pendente?.lead ?? null}
              clinica={{ config }}
              disabled={salvando}
              autoFocus
            />
            <p className="campo-ajuda">
              O código é o que faz a importação do {nomeDoSistema(config)} casar os pagamentos com este lead. A
              entrada NÃO é lançada como recebimento aqui: ela volta na importação, como parcela
              liquidada.
            </p>
          </>
        )}

        {paraAgendado && erroFuturos && <p className="aviso" role="alert">{erroFuturos}</p>}
        {avisoDePosse && <p className="aviso" data-campo="aviso-posse">{avisoDePosse}</p>}
        {deOutra?.assumirAntes && (
          <p className="ajuda" data-campo="assumir-ao-agendar">{posse?.texto} Ao agendar, o lead passa a ser seu.</p>
        )}
        {paraAgendado && (
          <CampoAgendamento
            grade={grade}
            agendamentosDoDia={agendaDoDia}
            dia={dia}
            aoTrocarDia={(v) => { setDia(v); setHora(''); }}
            hora={hora}
            aoEscolherHora={setHora}
            foraDaGrade={foraDaGrade}
            aoAlternarFora={(v) => { setForaDaGrade(v); setHora(''); }}
            tipo={tipo}
            aoTrocarTipo={setTipo}
            agendamentoFuturo={agendamentoFuturo}
            acao={acao}
            aoTrocarAcao={setAcao}
            carregando={carregandoAgenda}
          />
        )}

        {comMotivo && (
          <div className="campo">
            <label htmlFor="de-motivo">
              {paraNaoAgendou ? 'Por que não agendou?' : 'Por que não fechou?'}
            </label>
            <select id="de-motivo" required value={motivoAtual} onChange={(e) => setMotivo(e.target.value)}>
              {listaMotivos.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.rotulo}{m.encerra ? ' — encerra' : ''}
                </option>
              ))}
            </select>
            {listaMotivos.length === 0 && (
              <p className="erro">Nenhum motivo ativo configurado. Peça ao gestor em Configurações → Motivos do funil.</p>
            )}
            {motivoEncerra(config, pendente.etapa, motivoAtual) && (
              <p className="campo-ajuda">
                Este motivo <strong>encerra</strong> o lead: ele conta como perdido na conversão de valor.
              </p>
            )}
          </div>
        )}

        {pedeRetorno && (
          <div className="campo">
            <label htmlFor="de-retorno">Retorno em</label>
            <input
              id="de-retorno"
              type="datetime-local"
              required
              value={retornoAtual}
              onChange={(e) => setRetorno(e.target.value === '' ? null : e.target.value)}
            />
            <p className="campo-ajuda">
              Vira um próximo contato em Próximos contatos. Sugestão pela cadência da clínica — mude se souber quando ligar.
            </p>
          </div>
        )}

        {paraNegociacao && (
          <div className="campo">
            <label htmlFor="de-negociador">Negociador</label>
            <select
              id="de-negociador"
              required
              autoFocus
              value={negociadorAtual}
              onChange={(e) => setNegociadorId(e.target.value)}
            >
              <option value="">Escolha quem vai negociar…</option>
              {negociadores.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nome}
                </option>
              ))}
            </select>
            <p className="campo-ajuda">
              Quem compareceu e ainda não fechou fica com o negociador; os retornos automáticos
              vão para a fila dele.
            </p>
          </div>
        )}

        {/**
          * ★★ WH — ENTRAR EM "COMPARECEU" pede oportunidade, CÓDIGO e NOME confirmado.
          *
          * É o MESMO `CamposValores` do fechamento, no modo `comparecimento`: duplicar
          * "Código Paciente" num segundo arquivo seria a divergência que a guarda de fonte
          * da WD-c2 existe para impedir. Aqui morava um input solto (`de-orcado`), com a
          * ajuda dizendo "deixe em branco se o orçamento ainda não saiu" — frase que a
          * medição derrubou: 38 dos 59 comparecimentos de setembro ficaram em branco.
          *
          * ★ `nao_fechou` NÃO entra neste modo: a decisão do dono é sobre `compareceu`, e
          * quem vai para "não fechou" pode nunca ter tido orçamento registrado.
          */}
        {paraComparecimento && (
          <>
            <CamposValores
              valores={valores}
              aoMudar={setValores}
              lead={pendente?.lead ?? null}
              clinica={{ config }}
              disabled={salvando}
              momento="comparecimento"
            />
            <p className="campo-ajuda">
              O código é o que faz a importação do {nomeDoSistema(config)} casar os pagamentos com
              este lead. A oportunidade é o que mostra quanto do que a clínica orça vira
              contrato — e quanto saiu pela porta. Se não houve orçamento, marque a caixa.
            </p>
          </>
        )}

        {paraNaoFechou && (
          <div className="campo">
            {/* ★ WD-c2 (item 4) — RÓTULO CURTO, DE PROPÓSITO. "Valor de oportunidade (R$, opcional)" quebrava
                em duas linhas na largura do diálogo e empurrava a caixinha para baixo, desalinhada da do
                Negociador logo acima. "opcional" desceu para a ajuda, que já dizia a mesma coisa em
                palavras melhores. É layout: a prova está no teste de navegador real, medindo as alturas. */}
            <label htmlFor="de-orcado">Valor de oportunidade (R$)</label>
            <input
              id="de-orcado"
              inputMode="decimal"
              placeholder="ex.: 12000"
              value={orcadoAtual}
              onChange={(e) => setOrcado(e.target.value)}
            />
            <p className="campo-ajuda">
              Deixe em branco se o orçamento ainda não saiu — dá para preencher depois na ficha
              do lead, e o card fica marcado até lá. É este número que mostra quanto do que a
              clínica orça vira contrato, e quanto saiu pela porta.
            </p>
          </div>
        )}

        {erro && <p className="erro">{erro}</p>}

        <footer className="dialogo-acoes">
          <button type="button" className="btn-secundario" onClick={fechar}>
            Cancelar
          </button>
          {/* ★★★ WT 1b — A ETAPA NÃO MUDA SE A CONFERÊNCIA FALHOU.
              ⚠⚠ O aviso já existia (VQ), mas o botão continuava clicável: a pessoa lia
              "não foi possível conferir" e marcava a consulta de todo jeito — podendo
              duplicar uma consulta que já existia. ★ Mesma política do `DialogoCancelar`,
              que desabilita quando não sabe se era a última. */}
          <button type="submit" className="btn-primario" disabled={salvando || (paraAgendado && Boolean(erroFuturos))}>
            {salvando ? 'Salvando…' : deOutra?.assumirAntes && acao !== 'remarcar' ? 'Assumir e agendar' : 'Confirmar'}
          </button>
        </footer>
      </form>
    </Dialogo>
  );
}
